import useI18n from '@/shared/hooks/usei18n'
import { IconButton, InfoTooltip, Spinner, Tooltip } from '@/common'
import { CheckCircleIcon, SyncIcon } from '@/common/components/icons'
import FieldRangePill from '@/common/components/FieldRangePill/FieldRangePill'
import FieldRangeCaret from '@/common/components/FieldRangeCaret/FieldRangeCaret'
import { Quarter } from '@/api/models/EuaEmissionPriceOverview'
import type { EuaPriceQuarterData, EuaPriceQuartersState } from '@/shared/types/cbamModifiers'

const QUARTERS: Array<Quarter> = ['Q1', 'Q2', 'Q3', 'Q4']
type OfficialPriceQuarter2026 = 'Q1' | 'Q2' | 'Q3'
const OFFICIAL_PRICE_QUARTERS_2026: OfficialPriceQuarter2026[] = ['Q1', 'Q2', 'Q3']

export type EditableEuaQuarterField = 'value' | 'lowPercentage' | 'highPercentage'

export type { EuaPriceQuarterData, EuaPriceQuartersState }

function OfficialPriceQuarterControl({
  quarter,
  simulationYear,
  isLocked,
  isApplying,
  onApply
}: {
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4'
  simulationYear: number
  isLocked: boolean
  isApplying: boolean
  onApply: () => void
}) {
  const { t } = useI18n()
  const isEligible =
    OFFICIAL_PRICE_QUARTERS_2026.includes(quarter as OfficialPriceQuarter2026) &&
    simulationYear === 2026 &&
    !isLocked
  const tooltipClass = 'block text-left max-w-sm'

  if (isLocked) {
    return (
      <Tooltip
        content={
          <span className={tooltipClass}>
            {t(
              `cbamModifiers:editVariables.applyOfficialPriceLockedTooltip`
            )}
          </span>
        }
      >
        <span className="inline-flex">
          <CheckCircleIcon
            className="text-green-600 size-9"
            aria-label={t(
              `cbamModifiers:editVariables.applyOfficialPriceLockedAria`
            )}
          />
        </span>
      </Tooltip>
    )
  }

  if (isEligible) {
    if (isApplying) {
      return (
        <div className="flex items-center justify-center size-9" aria-busy="true">
          <Spinner className="w-5 h-5 text-green-600" />
        </div>
      )
    }
    return (
      <IconButton
        type="button"
        variant="subtle"
        sizeType="sm"
        icon={<SyncIcon className="size-6" />}
        onClick={onApply}
        aria-label={t(
          `cbamModifiers:editVariables.applyOfficialPriceApplyAria`
        )}
      />
    )
  }

  return (
    <Tooltip
      content={
        <span className={tooltipClass}>
          {t(
            `cbamModifiers:editVariables.applyOfficialPriceUnavailableTooltip`
          )}
        </span>
      }
    >
      <span className="inline-flex">
        <IconButton
          type="button"
          variant="neutral"
          disabled
          sizeType="sm"
          icon={<SyncIcon className="size-6" />}
          aria-label={t(
            `cbamModifiers:editVariables.applyOfficialPriceUnavailableAria`
          )}
        />
      </span>
    </Tooltip>
  )
}

function CertificatePriceQuarterRow({
  quarter,
  rowIndex,
  euaPriceQuarters,
  onQuarterChange,
  simulationYear,
  onApplyOfficialPrice,
  applyingOfficialQuarter
}: {
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4'
  rowIndex: number
  euaPriceQuarters: EuaPriceQuartersState
  onQuarterChange: (
    quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4',
    field: EditableEuaQuarterField,
    value: string
  ) => void
  simulationYear: number
  onApplyOfficialPrice: (quarter: OfficialPriceQuarter2026) => void
  applyingOfficialQuarter: OfficialPriceQuarter2026 | null
}) {
  const quarterData = euaPriceQuarters[quarter]
  const isLocked = Boolean(quarterData.isOfficialValueLocked)
  const applyingThisQuarter = applyingOfficialQuarter === quarter
  const rowBorder = rowIndex > 0 ? 'border-t border-gray-200' : ''

  return (
    <tr>
      <td
        className={`py-3 pr-4 align-middle font-semibold tabular-nums text-gray-900 ${rowBorder}`}
      >
        {quarter}
      </td>
      <td className={`py-3 pr-4 align-middle ${rowBorder}`}>
        <div>
          <FieldRangePill
            value={quarterData.value ?? 0}
            onChange={value =>
              onQuarterChange(
                quarter,
                'value',
                value === undefined || value === null ? '' : String(value)
              )
            }
            controls={{ step: 1, decimalScale: 2 }}
            disabled={isLocked}
          />
        </div>
      </td>
      <td className={`px-1 py-3 align-middle ${rowBorder}`}>
        <div className="w-25">
          <FieldRangeCaret
            value={
              quarterData.lowPercentage !== '' ? parseFloat(quarterData.lowPercentage) : undefined
            }
            onChange={value =>
              onQuarterChange(quarter, 'lowPercentage', value != null ? String(value) : '')
            }
            controls={{ min: 0, max: 100, step: 1 }}
            disabled={isLocked}
          />
        </div>
      </td>
      <td className={`px-1 py-3 align-middle ${rowBorder}`}>
        <div className="w-25">
          <FieldRangeCaret
            value={
              quarterData.highPercentage !== '' ? parseFloat(quarterData.highPercentage) : undefined
            }
            onChange={value =>
              onQuarterChange(quarter, 'highPercentage', value != null ? String(value) : '')
            }
            controls={{ min: 0, max: 100, step: 1 }}
            disabled={isLocked}
          />
        </div>
      </td>
      <td className={`py-3 align-middle ${rowBorder}`}>
        <div className="flex min-h-9 items-center justify-center">
          <OfficialPriceQuarterControl
            quarter={quarter}
            simulationYear={simulationYear}
            isLocked={isLocked}
            isApplying={applyingThisQuarter}
            onApply={() =>
              onApplyOfficialPrice(quarter as OfficialPriceQuarter2026)
            }
          />
        </div>
      </td>
    </tr>
  )
}

export default function EuaCertificatePriceTable({
  simulationYear,
  euaPriceQuarters,
  onQuarterChange,
  onApplyOfficialPrice,
  applyingOfficialQuarter
}: {
  simulationYear: number
  euaPriceQuarters: EuaPriceQuartersState
  onQuarterChange: (
    quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4',
    field: EditableEuaQuarterField,
    value: string
  ) => void
  onApplyOfficialPrice: (quarter: OfficialPriceQuarter2026) => void
  applyingOfficialQuarter: OfficialPriceQuarter2026 | null
}) {
  const { t } = useI18n()

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse text-sm table-fixed">
        <colgroup>
          <col className="w-18" />
          <col className="w-48" />
          <col className="w-36" />
          <col className="w-36" />
          <col className="w-38" />
        </colgroup>
        <thead>
          <tr className="text-gray-600">
            <th scope="col" className="pb-3 pr-4 text-left align-bottom font-medium">
              {t(`cbamModifiers:editVariables.quarterColumnHeader`)}
            </th>
            <th scope="col" className="pb-3 pr-4 text-left align-bottom font-medium">
              {t(`cbamModifiers:editVariables.priceColumnTitle`)}
            </th>
            <th
              scope="col"
              className="px-1 pb-3 text-left align-bottom text-sm font-medium leading-tight"
            >
              {t(`cbamModifiers:editVariables.lowPercentage`)}
            </th>
            <th
              scope="col"
              className="px-1 pb-3 text-left align-bottom text-sm font-medium leading-tight"
            >
              {t(`cbamModifiers:editVariables.highPercentage`)}
            </th>
            <th scope="col" className="pb-3 text-center align-bottom font-medium">
              <span className="inline-flex flex-wrap items-center justify-center gap-2">
                {t(
                  `cbamModifiers:editVariables.applyOfficialPriceColumnTitle`
                )}
                <InfoTooltip
                  content={
                    <span className="block max-w-sm text-left">
                      {t(
                        `cbamModifiers:editVariables.applyOfficialPriceColumnInfo`
                      )}
                    </span>
                  }
                  position="top"
                  size="medium"
                />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {QUARTERS.map((quarter, rowIndex) => (
            <CertificatePriceQuarterRow
              key={quarter}
              quarter={quarter}
              rowIndex={rowIndex}
              euaPriceQuarters={euaPriceQuarters}
              onQuarterChange={onQuarterChange}
              simulationYear={simulationYear}
              onApplyOfficialPrice={onApplyOfficialPrice}
              applyingOfficialQuarter={applyingOfficialQuarter}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}
